import type { Field, MediaRule } from "@ondit/publishing";
import { request } from "./index";
export interface DistributionDraft {
	platform_id: string;
	format: string;
	fields: Record<string, string>;
	media: { url: string; mime?: string; type?: string }[];
	evidence: { material_id: string; evidence: string }[];
	missing_information: string[];
	issues?: string[];
}
export interface DistributionChoice {
	platform_id: string;
	format: string;
	reason: string;
	platform: {
		name: string;
		languages: string[];
		verified: boolean;
		workflow_id: string | null;
		content_types: {
			id: string;
			label: [string, string];
			preview: {
				layout: string;
				fields: Field[];
				media: MediaRule | null;
			};
		}[];
	};
}
export interface DistributionBrief {
	id: string;
	created_at?: string;
	operation?: {
		id: string;
		kind: "recommend" | "adapt";
		state: "queued" | "running" | "completed" | "failed";
		stage: string;
		created_at: string;
		started_at?: string;
		finished_at?: string;
		error?: string;
	};
	revision: number;
	state: string;
	input: {
		additional_assets?: { file_id: string; url: string; type: string; mime: string }[];
		materials: {
			id: string;
			revision: number;
			title: string;
			assets?: { file_id: string; url: string; type: string; mime: string }[];
		}[];
		goal: string;
		language: string;
		min_count: number;
		max_count: number;
	};
	analysis?: { purpose?: string; missing_information?: string[] };
	recommendations?: DistributionChoice[];
	selected?: DistributionChoice[];
	channels?: { platform_id: string; platform_name: string; format: string }[];
	shortfall_reason?: string;
	drafts?: DistributionDraft[];
	approved_revision?: number;
	plan?: {
		revision: number;
		items: {
			platform_id: string;
			account_id?: string;
			account_source?: "customer" | "managed_pool";
			scheduled_at: string;
		}[];
	};
	pending_plan?: DistributionBrief["plan"];
	jobs?: { platform_id: string; job_id: string; status?: { state: string; published_url?: string; error?: string } }[];
}
export interface DistributionOperation {
	id: string;
	brief_id: string;
	kind: "recommend" | "adapt" | "publish";
	state:
		| "queued"
		| "running"
		| "completed"
		| "failed"
		| "waiting_human"
		| "published"
		| "remote_draft"
		| "unknown"
		| "cancelled";
	stage: string;
	scheduled_at?: string;
	published_url?: string;
	platform_id?: string;
	platform_name?: string;
	error?: string;
	created_at: string;
	started_at?: string;
	finished_at?: string;
	language: string;
	min_count: number;
	max_count: number;
	materials: { id: string; title: string }[];
	source?: { kind: "interface" | "workflow"; run_id?: string };
}
const path = (entity: string) => `/entities/${encodeURIComponent(entity)}/distribution/briefs`;
const post = <T>(url: string, body: unknown) =>
	request<T>(url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
export const smartDistribution = {
	operations: (entity: string) =>
		request<DistributionOperation[]>(`/entities/${encodeURIComponent(entity)}/distribution/operations`),
	list: (entity: string) => request<DistributionBrief[]>(path(entity)),
	read: (entity: string, id: string) => request<DistributionBrief>(`${path(entity)}/${encodeURIComponent(id)}`),
	create: (
		entity: string,
		body: {
			request_id: string;
			page_ids: string[];
			channels?: { platform_id: string; format: string }[];
			language: string;
			min_count: number;
			max_count: number;
		},
	) => post<DistributionBrief>(path(entity), body),
	change: (
		entity: string,
		id: string,
		action: "adapt" | "review" | "plan" | "cancel" | "remove_channel" | "reschedule" | "recover",
		body: unknown,
	) => post<DistributionBrief>(`${path(entity)}/${encodeURIComponent(id)}/${action}`, body),
};
